import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Stack, CardContent } from '@mui/material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { ContractOverview, ContractCreateHeader, ContractTabSection } from '../components/sections';
import { useAppSelector, useAppDispatch } from '../../../app/store';
import { initializeContract, clearSelectedContract } from '../../../services/ContractService/contractSlice';
import { PageLoading as Loading } from '../../common/components';

// Component for handling missing company parameter
const MissingParameterError: React.FC<{ company: string }> = ({ company }) => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Missing required parameters. Please specify company to create a contract.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
            Company: {company || 'Not specified'}
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

// Component for loading state
const LoadingState: React.FC = () => <Loading />;

// Main content component
const ContractCreateContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  
  // Redux state
  const contract = useAppSelector((state) => state.contract.currentContract);
  const isEditing = useAppSelector((state) => state.contract.isEditing);
  const isDirty = useAppSelector((state) => state.contract.isDirty);
  
  // URL parameters
  const company = searchParams.get('company') || '';

  // Initialize contract on mount and cleanup on unmount
  useEffect(() => {
    if (company) {
      dispatch(initializeContract({ company }));
    }
    
    return () => {
      dispatch(clearSelectedContract());
    };
  }, [dispatch, company]);

  // Handle missing company parameter
  if (!company) {
    return <MissingParameterError company={company} />;
  }

  // Render main contract creation interface
  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <ContractCreateHeader company={company} isDirty={isDirty} />
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {contract && isEditing ? (
              <Stack spacing={3}>
                <ContractOverview />
                <ContractTabSection />
              </Stack>
            ) : (
              <LoadingState />
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

// Main component with authentication wrapper
const ContractCreate: React.FC = () => (
  <AuthenticatedTemplate>
    <ContractCreateContent />
  </AuthenticatedTemplate>
);

export default ContractCreate;
